import 'react-toastify/dist/ReactToastify.min.css';
import * as Sentry from '@sentry/react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom';
import { Suspense, lazy } from 'react';

import AppFallback from './components/AppFallback';
import CenteredPage from './components/CenteredPage';
import CreateNewTeamPage from './pages/CreateNewTeamPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import PageLoading from './components/PageLoading';
import SecuredRoutes from './components/SecuredRoutes';
import SignUpPage from './pages/SignUpPage';
import Snackbars from './components/feedback/Snackbars';
import JoinLinkPage from './pages/JoinLinkPage';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ThemeProvider from './contexts/ThemeProvider';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const TeamLoginPage = lazy(() => import('./pages/TeamLoginPage'));
const TokensPage = lazy(() => import('./pages/dev/TokensPage'));
const ComponentsPage = lazy(() => import('./pages/dev/ComponentsPage'));
const EmailConfirmedPage = lazy(() => import('./pages/EmailConfirmedPage'));
const ClaimInvitationPage = lazy(() => import('./pages/ClaimInvitationPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const InvitationSignUpPage = lazy(() => import('./pages/InvitationSignUpPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    // Mutations stay on 'online': they don't retry, so offline-first would
    // fail a save made offline instead of holding it until reconnect.
    queries: { networkMode: 'offlineFirst' },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Suspense
          fallback={
            <CenteredPage>
              <PageLoading>Please wait...</PageLoading>
            </CenteredPage>
          }
        >
          <Snackbars />
          <Sentry.ErrorBoundary
            showDialog
            fallback={({ error }) => <AppFallback error={error} />}
          >
            <Router>
              <Switch>
                <Route path="/login" exact component={LoginPage} />
                <Route path="/login/teams" exact component={TeamLoginPage} />
                <Route path="/join/:code" exact component={JoinLinkPage} />
                <Route path="/login/teams/new" exact>
                  <CreateNewTeamPage />
                </Route>
                <Route
                  path="/confirmation"
                  exact
                  component={EmailConfirmedPage}
                />
                <Route path="/signup" exact>
                  <SignUpPage />
                </Route>
                <Route path="/invitations" exact>
                  <ClaimInvitationPage />
                </Route>
                <Route path="/invitations/signup" exact>
                  <InvitationSignUpPage />
                </Route>
                <Route path="/forgot_password" exact>
                  <ForgotPasswordPage />
                </Route>
                <Route path="/reset_password" exact>
                  <ResetPasswordPage />
                </Route>
                {/* Design token and component fixtures, not linked from the nav */}
                <Route path="/dev/tokens" exact component={TokensPage} />
                <Route
                  path="/dev/components"
                  exact
                  component={ComponentsPage}
                />
                <SecuredRoutes />
              </Switch>
            </Router>
          </Sentry.ErrorBoundary>
        </Suspense>
        <ReactQueryDevtools initialIsOpen={false} />
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
